<script setup lang="ts">
import { convertSchemaToHtml } from '@thebeyondgroup/shopify-rich-text-renderer'
import { computed, ref } from 'vue'

import type { AddToNextOrderAction } from '@/lib/addToNextOrderAction'
import type { Product } from '@/services/product'
import type { CollectionFreeGift } from '@/services/storefront'
import type { Subscription } from '@/services/subscription'

import Button from '@/components/Button.vue'
import Label from '@/components/Label.vue'
import Message from '@/components/Message.vue'
import Modal from '@/components/Modal.vue'
import ProductImage from '@/components/ProductImage.vue'
import QuantityControl from '@/components/QuantityControl.vue'

import { formatDate } from '@/lib/formatters/date'
import { prettyFrequency } from '@/lib/formatters/frequency'
import { formatMoneyWithCurrency } from '@/lib/formatters/money'
import { formatSellingPlanName } from '@/lib/formatters/sellingPlan'
import { extractShopifyId } from '@/lib/gid'
import { getOriginalPrice, getPrice } from '@/lib/sellingPlans'
import { AsyncOperationState } from '@/stores/lib/AsyncOperationState'

const props = defineProps<{
  selectedProduct: Product
  existingSubscriptions: Subscription[] | null
  nextOrderScheduledAt: string | null
  addToNextOrderState: AsyncOperationState
  offerPercent?: number
  freeGift?: CollectionFreeGift | null
  introText?: string | null
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'add-to-next-order', action: AddToNextOrderAction): void
}>()

const sellingPlanEdges = computed(
  () =>
    props.selectedProduct.sellingPlanGroups.edges[0]?.node.sellingPlans.edges ??
    [],
)
const hasSellingPlans = computed(() => sellingPlanEdges.value.length > 0)

const selectedVariant = ref<Product['variants']['edges'][number]['node']>(
  props.selectedProduct.variants.edges[0].node,
)

const justThisOrder = 'just-this-order'
const selectedSellingPlanId = ref(
  sellingPlanEdges.value[0]?.node.id ?? justThisOrder,
)
const selectedSellingPlan = computed(
  () =>
    sellingPlanEdges.value.find(
      (edge) => edge.node.id === selectedSellingPlanId.value,
    )?.node ?? null,
)

const quantity = ref(1)

const existingSubscription = computed(() => {
  return props.existingSubscriptions
    ? (props.existingSubscriptions.find(
        (s) =>
          s.external_variant_id.ecommerce ===
          extractShopifyId(selectedVariant.value?.id ?? ''),
      ) ?? null)
    : null
})

const nextOrderDate = computed(() =>
  props.nextOrderScheduledAt
    ? formatDate('YYYY-MM-DD', props.nextOrderScheduledAt)
    : null,
)
const existingSubscriptionDate = computed(() =>
  existingSubscription.value?.next_charge_scheduled_at
    ? formatDate(
        'YYYY-MM-DD',
        existingSubscription.value.next_charge_scheduled_at,
      )
    : null,
)
const existingSubscriptionDueInNextOrder = computed(
  () =>
    !!nextOrderDate.value &&
    !!existingSubscriptionDate.value &&
    existingSubscriptionDate.value === nextOrderDate.value,
)

const showOfferBadge = computed(
  () => !!props.offerPercent && !!selectedSellingPlan.value,
)

const displayPrice = computed(() => {
  const variantId = selectedVariant.value.id
  if (!selectedSellingPlan.value) {
    return getOriginalPrice(props.selectedProduct, variantId)
  }
  const subscriptionPrice = getPrice(
    props.selectedProduct,
    variantId,
    selectedSellingPlan.value.id,
  )
  if (props.offerPercent) {
    return Math.round(subscriptionPrice * (1 - props.offerPercent / 100))
  }
  return subscriptionPrice
})

const isConfirmDisabled = computed(
  () =>
    !props.nextOrderScheduledAt ||
    existingSubscriptionDueInNextOrder.value ||
    !selectedVariant.value.availableForSale,
)

const introHtml = computed(() => {
  if (!props.introText) return null
  try {
    return convertSchemaToHtml(props.introText)
  } catch {
    return null
  }
})

const confirmSelection = () => {
  if (
    props.addToNextOrderState === AsyncOperationState.IN_PROGRESS ||
    isConfirmDisabled.value
  ) {
    return
  }

  if (existingSubscription.value && !existingSubscriptionDueInNextOrder.value) {
    emit('add-to-next-order', {
      type: 'move-existing-subscription',
      variantId: selectedVariant.value.id,
      subscriptionId: existingSubscription.value.id,
      quantity: existingSubscription.value.quantity,
    })
    return
  }

  if (selectedSellingPlan.value) {
    emit('add-to-next-order', {
      type: 'add-new-subscription',
      variantId: selectedVariant.value.id,
      selectedSellingPlan: selectedSellingPlan.value,
      quantity: quantity.value,
    })
    return
  }

  emit('add-to-next-order', {
    type: 'add-onetime',
    variantId: selectedVariant.value.id,
    quantity: quantity.value,
  })
}
</script>

<template>
  <Modal title="Add to your order" @close="emit('close')">
    <div class="AddToOrderModal">
      <!-- eslint-disable vue/no-v-html -->
      <div
        v-if="introHtml"
        class="AddToOrderModal__Intro"
        v-html="introHtml"
      ></div>
      <!-- eslint-enable vue/no-v-html -->
      <div class="AddToOrderModal__Product">
        <ProductImage
          class="AddToOrderModal__Image"
          :url="selectedProduct.images.edges[0]?.node.small"
          :alt="
            selectedProduct.images.edges[0]?.node.altText ??
            selectedProduct.title
          "
        />
        <p class="AddToOrderModal__ProductTitle">
          {{ selectedProduct.title }}
        </p>
      </div>

      <div
        v-if="selectedProduct.variants.edges.length > 1"
        class="AddToOrderModal__Select"
      >
        <label>Select variant</label>
        <select v-model="selectedVariant">
          <option
            v-for="variant in selectedProduct.variants.edges"
            :key="variant.node.id"
            :value="variant.node"
          >
            {{ variant.node.title }}
          </option>
        </select>
      </div>

      <template
        v-if="
          existingSubscription && existingSubscription.next_charge_scheduled_at
        "
      >
        <Message mode="light-alt">
          <p><strong>You’re subscribed to this product.</strong></p>
          <p>
            You get one of these every
            {{
              prettyFrequency(
                existingSubscription.order_interval_frequency,
                existingSubscription.order_interval_unit,
              )
            }}. Your next delivery is on
            {{
              formatDate(
                'DD Month',
                existingSubscription.next_charge_scheduled_at,
              )
            }}.
          </p>
        </Message>
      </template>

      <template v-if="!selectedVariant.availableForSale">
        <Message mode="warning">
          <p>This product is not currently available.</p>
        </Message>
      </template>

      <div v-else class="AddToOrderModal__Card">
        <div class="AddToOrderModal__CardHeader">
          <p class="AddToOrderModal__CardTitle">Add to next order</p>
          <Label v-if="showOfferBadge" mode="warning-alt" class="Label--small">
            {{ offerPercent }}% off
          </Label>
          <span class="AddToOrderModal__Price">
            {{ formatMoneyWithCurrency(displayPrice / 100) }}
          </span>
        </div>

        <div v-if="!existingSubscription" class="AddToOrderModal__Quantity">
          <span>Send me</span>
          <QuantityControl
            :value="quantity"
            :is-busy="false"
            :debounce-delay="1"
            @change-quantity="quantity = $event"
          />
          <select
            v-if="hasSellingPlans"
            v-model="selectedSellingPlanId"
            class="AddToOrderModal__Cadence"
          >
            <option
              v-for="sellingPlan in sellingPlanEdges"
              :key="sellingPlan.node.id"
              :value="sellingPlan.node.id"
            >
              {{ formatSellingPlanName(sellingPlan.node.name) }}
            </option>
            <option :value="justThisOrder">just this order</option>
          </select>
        </div>

        <div class="AddToOrderModal__Actions">
          <Button
            class="AddToOrderModal__Cancel"
            :modifiers="['link']"
            @click="emit('close')"
          >
            Cancel
          </Button>
          <Button
            class="AddToOrderModal__Confirm"
            size="large"
            :loading="addToNextOrderState === AsyncOperationState.IN_PROGRESS"
            :disabled="isConfirmDisabled"
            @click="confirmSelection"
          >
            Confirm selection
          </Button>
        </div>
      </div>

      <div
        v-if="freeGift && !existingSubscription"
        class="AddToOrderModal__Gift"
      >
        <p class="AddToOrderModal__GiftHeading">Your free gift</p>
        <div class="AddToOrderModal__GiftCard">
          <ProductImage
            class="AddToOrderModal__GiftImage"
            :url="freeGift.imageUrl ?? undefined"
            :alt="freeGift.imageAlt ?? freeGift.title"
          />
          <div class="AddToOrderModal__GiftCopy">
            <p class="AddToOrderModal__GiftTitle">{{ freeGift.title }}</p>
            <p class="AddToOrderModal__GiftMeta">
              <template v-if="freeGift.price">
                Worth {{ formatMoneyWithCurrency(freeGift.price) }} ·
              </template>
              added automatically
            </p>
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style>
.AddToOrderModal {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.AddToOrderModal__Intro {
  margin: 4px 0 0;
  color: var(--color-forest-100);
  font-size: 16px;
  font-weight: 700;
  line-height: 1.35;

  & p {
    margin: 0;
  }

  & a {
    font-weight: 700;
  }
}

.AddToOrderModal__Product {
  display: flex;
  align-items: center;
  gap: 16px;
}

.AddToOrderModal__Image {
  width: 88px;
  height: 88px;
  border-radius: 12px;
  object-fit: cover;
  flex-shrink: 0;
}

.AddToOrderModal__ProductTitle {
  margin: 0;
  color: var(--color-forest-100);
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
}

.AddToOrderModal__Select {
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: stretch;
}

.AddToOrderModal__Card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border-radius: 16px;
  background: var(--color-lichen-25);
  border: 1px solid var(--color-lichen-100);
}

.AddToOrderModal__CardHeader {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.AddToOrderModal__CardTitle {
  margin: 0;
  color: var(--color-forest-100);
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
}

.AddToOrderModal__Price {
  margin-left: auto;
  color: var(--color-forest-100);
  font-size: 18px;
  font-weight: 400;
  line-height: 1.2;
}

.AddToOrderModal__Quantity {
  display: flex;
  align-items: center;
  gap: 10px;

  & .QuantityControl {
    border-radius: 9999em;
    flex-shrink: 0;
  }
}

.AddToOrderModal__Cadence {
  appearance: none;
  flex: 1;
  min-width: 0;
  background-color: var(--color-white);
  border: 1px solid var(--color-lichen-150);
  border-radius: 9999em;
  padding: 8px 12px;
  color: var(--color-forest-100);
  font-family: var(--font-family-rund);
  font-size: var(--font-size--base);
  line-height: var(--line-height--base);
}

.AddToOrderModal__Actions {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid var(--color-lichen-100);
}

.AddToOrderModal__Cancel.Button {
  color: var(--color-forest-100);
  font-weight: 700;
  padding: 12px 16px;
  flex-shrink: 0;

  &:enabled:hover,
  &:enabled:focus {
    color: var(--color-forest-80);
    text-decoration: none;
  }
}

.AddToOrderModal__Confirm.Button {
  flex: 1;
}

.AddToOrderModal__Gift {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.AddToOrderModal__GiftHeading {
  margin: 0;
  color: var(--color-forest-100);
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
}

.AddToOrderModal__GiftCard {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-radius: 16px;
  background: var(--color-sand-100);
  border: 1px solid var(--color-lichen-100);
}

.AddToOrderModal__GiftImage {
  width: 64px;
  height: 64px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
}

.AddToOrderModal__GiftCopy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.AddToOrderModal__GiftTitle,
.AddToOrderModal__GiftMeta {
  margin: 0;
}

.AddToOrderModal__GiftTitle {
  color: var(--color-forest-100);
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
}

.AddToOrderModal__GiftMeta {
  color: var(--color-forest-80);
  font-size: 14px;
  line-height: 1.4;
}
</style>
